import React, {
  FunctionComponent, useEffect, useState,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColGroupDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  XdpPrivileges,
} from 'data/xdp/xdp.types';
import * as actions from 'data/xdp/xdp.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/xdp/xdp.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import DistributorGrid from './components/XdpGrid';
import { xdpColumnDefs } from '../../Xdp.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getXdps(state),
  selectedXdp: selectors.getSelectedXdp(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  goToXdpCreate: actions.goToXdpCreate,
  goToXdpEdit: actions.goToXdpEdit,
  goToXdpView: actions.goToXdpView,
  selectXdp: actions.selectXdp,
  getXdpList: actions.getXdps,
  deleteXdp: actions.deleteXdp,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const XdpList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedXdp,
  goToXdpCreate,
  goToXdpEdit,
  goToXdpView,
  selectXdp,
  getXdpList,
  deleteXdp,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColGroupDef[]>(xdpColumnDefs);

  useEffect(() => {
    getXdpList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getXdpList]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getXdpList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToXdpCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectXdp(event.api.getSelectedRows());
  };

  const handleDeleteXdp = () => {
    const distributorConfigrationId = selectedXdp[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedXdp[0]?.ipduId,
        AuditService.XDP,
        AuditActionType.XDP_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteXdp(deliveryFunctionId, distributorConfigrationId, body);
  };

  const handleGoToEdit = () => goToXdpEdit(history);

  const handleGoToView = () => goToXdpView(history);

  const hasPrivilegeToView = privileges.includes(XdpPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(XdpPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(XdpPrivileges.Delete);

  return (
    <div data-testid="XdpList" className="GridPageContainer">
      <DistributorGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedXdp={selectedXdp[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteXdp}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </div>
  );
};

export default withRouter(connector(XdpList));
